/**
 * Poner el cursor en el redactor de Intercom al abrirlo, para que salga el teclado; o, para una
 * nota de voz, pulsar su micrófono (startIntercomVoiceNote, más abajo); o escribir y enviar el
 * mensaje de la Ayuda en una conversación que ya existe (replyInIntercomConversation).
 *
 * En una tablet, abrir el chat y tener que buscar el campo con el dedo antes de poder escribir es
 * fricción justo en el momento en que la persona ya ha decidido contar algo. Con el foco puesto, el
 * teclado sale solo y se escribe directamente.
 *
 * Que esto sea posible no es evidente: el messenger vive en un iframe, pero Intercom lo crea SIN
 * `src` y escribe su DOM por JavaScript, así que es del MISMO ORIGEN que la página y su contenido
 * es accesible. Comprobado sobre el widget real: el redactor es un `contenteditable` y `focus()`
 * desde la página funciona. Si fuese un iframe de otro origen, nada de esto se podría hacer.
 *
 * Aun así es un adorno sobre el DOM de un tercero, no un contrato: Intercom puede renombrar o
 * reestructurar lo que quiera en cualquier despliegue. De ahí las tres reglas de este módulo:
 * sondeo acotado, nunca lanza, y si no encuentra el campo se calla. El mensaje ya se ha enviado
 * antes de llegar aquí; esto solo ahorra un toque.
 */

const FRAME = 'iframe[name="intercom-messenger-frame"]';
const FIELD = 'textarea, [contenteditable="true"]';
const BUTTON = 'button, [role="button"]';

// El micrófono de las Voice notes (Workspace › Security › Attachments › Messenger input types).
// Medido sobre el widget real el 29-sep-2026:
//  - SOLO aparece con el redactor vacío: con texto escrito, su sitio lo ocupa «enviar». Por eso
//    la nota de voz no puede ir en un redactor prerrellenado.
//  - Su nombre accesible es «Iniciar grabación» / «Démarrer l’enregistrement» / «Start recording»
//    según el idioma del messenger; el icono (un micro) es el mismo en todos. Se busca por las dos
//    cosas, para sobrevivir a un cambio de texto o de icono, no de los dos a la vez.
//  - Un `click()` lanzado desde la página arranca la grabación: Intercom consulta el permiso y
//    llama a getUserMedia({ audio: true }) dentro de su iframe (del mismo origen, así que el
//    permiso de micrófono que se pide es el de nuestra app).
// Anclada al verbo de EMPEZAR: mientras graba, el mismo messenger enseña «Detener / Eliminar /
// Reproducir grabación», y pulsar uno de esos pararía o borraría la nota que se está grabando.
const MIC_LABEL = /^(iniciar|empezar|comenzar|start|begin|démarrer|commencer)\b[^]*?(grabaci|record|enregistr)/i;
const MIC_PATH = 'M6 9C7.66 9 9 7.66 9 6V3';

// El botón de enviar del redactor, medido el 30-sep-2026: «Enviar un mensaje…» / «Envoyer un
// message…» / «Send a message…», deshabilitado con el redactor vacío, con la clase
// `intercom-composer-send-button` (de las pocas que no son un hash) y una flecha hacia arriba. Tres
// señales, para sobrevivir a que cambie una.
const SEND_LABEL = /^(enviar|envoyer|send)\b/i;
const SEND_CLASS = 'intercom-composer-send-button';
const SEND_PATH = 'M7.4 1.899a.85.85 0 0 1 1.201 0l4.5 4.5';

const visible = (el) => Boolean(el && (el.offsetWidth || el.offsetHeight));

// Oculto con Intercom('hide'), el iframe se QUEDA en el DOM a 0×0 con visibility:hidden, y lo de
// dentro sigue midiendo como visible (medido: el micro seguía dando 24×24). Sin esta comprobación
// se enfocaría el redactor, o se pulsaría el micro, de una conversación que nadie ve.
function frameShown(frame) {
  try {
    const box = typeof frame.getBoundingClientRect === 'function' ? frame.getBoundingClientRect() : null;
    if (box && (box.width === 0 || box.height === 0)) return false;
    const view = frame.ownerDocument && frame.ownerDocument.defaultView;
    const style = view && typeof view.getComputedStyle === 'function' ? view.getComputedStyle(frame) : null;
    return !(style && (style.visibility === 'hidden' || style.display === 'none'));
  } catch (_) {
    return true;
  }
}

// Acceder a contentDocument de un iframe de otro origen lanza: si algún día Intercom cambia a
// uno con `src`, esto deja de encontrar nada en vez de romper la página.
function messengerDocument(doc) {
  const frame = doc.querySelector(FRAME);
  if (!frame || !frameShown(frame)) return null;
  try {
    return frame.contentDocument || null;
  } catch (_) {
    return null;
  }
}

/** El campo de escritura del messenger, si está en el DOM y es visible. */
function findComposer(doc) {
  const inner = messengerDocument(doc);
  if (!inner) return null;
  return [...inner.querySelectorAll(FIELD)].find(visible) || null;
}

const enabled = (el) => !(typeof el.hasAttribute === 'function' && el.hasAttribute('disabled'))
  && el.getAttribute('aria-disabled') !== 'true';

const pathOf = (b) => {
  const path = b.querySelector && b.querySelector('svg path');
  return (path && path.getAttribute('d')) || '';
};

/**
 * El botón del micrófono del redactor, si está en el DOM, visible y usable. Uno deshabilitado (el
 * redactor bloqueado mientras Fin prepara su respuesta, por ejemplo) no cuenta: pulsarlo no graba.
 */
function findVoiceButton(doc) {
  const inner = messengerDocument(doc);
  if (!inner) return null;
  const buttons = [...inner.querySelectorAll(BUTTON)].filter((b) => visible(b) && enabled(b));
  return buttons.find((b) => MIC_LABEL.test(b.getAttribute('aria-label') || ''))
    || buttons.find((b) => pathOf(b).startsWith(MIC_PATH))
    || null;
}

/** El botón de enviar, si está visible y usable (con el redactor vacío está deshabilitado). */
function findSendButton(doc) {
  const inner = messengerDocument(doc);
  if (!inner) return null;
  const buttons = [...inner.querySelectorAll(BUTTON)].filter((b) => visible(b) && enabled(b));
  const classOf = (b) => String((b.getAttribute && b.getAttribute('class')) || '');
  // De la señal más propia del redactor a la más laxa: un «Enviar» de otra cosa en el hilo (una
  // valoración de Fin, un formulario) no es el botón del redactor.
  return buttons.find((b) => classOf(b).split(/\s+/).includes(SEND_CLASS))
    || buttons.find((b) => pathOf(b).startsWith(SEND_PATH))
    || buttons.find((b) => SEND_LABEL.test(b.getAttribute('aria-label') || ''))
    || null;
}

const fieldText = (field) => String((field && (field.value !== undefined ? field.value : field.textContent)) || '');

function composerEmpty(doc) {
  const field = findComposer(doc);
  if (!field) return true;
  return !fieldText(field).trim();
}

function messengerHasText(doc, snippet) {
  if (!snippet) return true;
  const inner = messengerDocument(doc);
  const body = inner && inner.body;
  return Boolean(body && String(body.textContent || '').includes(snippet));
}

// Intercom pinta cada línea en su propio párrafo y el texto de la página los junta sin separador,
// así que se compara línea a línea y con los espacios normalizados.
const squash = (text) => String(text || '').replace(/\s+/g, ' ').trim();

function countIn(haystack, needle) {
  if (!needle) return 0;
  let count = 0;
  for (let at = haystack.indexOf(needle); at !== -1; at = haystack.indexOf(needle, at + needle.length)) count += 1;
  return count;
}

/**
 * Cuántas veces está `line` en el HILO: lo que hay en el messenger menos lo que hay en el redactor. Solo
 * se resta un redactor contenteditable, porque su texto es parte del de la página; el de un
 * `<textarea>` va en su `value`, no ahí, y restarlo contaría un mensaje enviado como no enviado.
 */
function timesOnThread(doc, line) {
  const inner = messengerDocument(doc);
  const body = inner && inner.body;
  const needle = squash(line);
  const field = findComposer(doc);
  const inField = field && field.value === undefined ? countIn(squash(fieldText(field)), needle) : 0;
  return countIn(squash(body && body.textContent), needle) - inField;
}

/** ¿Está esta línea en el hilo de la conversación que enseña el messenger (no en su redactor)? */
export function messengerHasLine(line, doc = typeof document !== 'undefined' ? document : null) {
  try {
    return Boolean(doc && squash(line)) && timesOnThread(doc, line) > 0;
  } catch (_) {
    return false;
  }
}

// Para quitar del redactor lo que pegó la app: se selecciona su contenido y, en la vuelta siguiente
// (Lexical se entera de la selección con `selectionchange`, que llega después), se corta. Medido sobre
// el widget real: `execCommand('delete')` no lo borra; un `cut` sintético, sí.
function selectContents(field) {
  const owner = field.ownerDocument;
  const view = owner && owner.defaultView;
  if (!owner || !view || typeof owner.createRange !== 'function' || typeof view.getSelection !== 'function') return;
  field.focus({ preventScroll: true });
  const range = owner.createRange();
  range.selectNodeContents(field);
  const selection = view.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
}

function cutFrom(field) {
  const view = field.ownerDocument && field.ownerDocument.defaultView;
  if (!view || typeof view.DataTransfer !== 'function' || typeof view.ClipboardEvent !== 'function') return;
  field.dispatchEvent(new view.ClipboardEvent('cut', { clipboardData: new view.DataTransfer(), bubbles: true, cancelable: true }));
}

/**
 * Mete `text` en el redactor como lo haría pegarlo. El redactor es un editor Lexical: con
 * `execCommand('insertText')` el texto entra pero se pierden los saltos de línea (y con ellos alguna
 * línea entera, medido); pegado, Lexical crea un párrafo por línea, que es lo que llega a soporte.
 */
function pasteInto(field, text) {
  const view = field.ownerDocument && field.ownerDocument.defaultView;
  if (!view || typeof view.DataTransfer !== 'function' || typeof view.ClipboardEvent !== 'function') return false;
  const data = new view.DataTransfer();
  data.setData('text/plain', text);
  field.focus({ preventScroll: true });
  field.dispatchEvent(new view.ClipboardEvent('paste', { clipboardData: data, bubbles: true, cancelable: true }));
  return true;
}

/** ¿Está el messenger a la vista? (Oculto, su iframe sigue en el DOM a 0×0.) */
export function messengerShown(doc = typeof document !== 'undefined' ? document : null) {
  try {
    return Boolean(doc && messengerDocument(doc));
  } catch (_) {
    return false;
  }
}

/**
 * Pulsa el micrófono del messenger por la persona, para que la nota de voz empiece a grabar sin
 * buscar el botón. Mismo contrato que el foco: sondeo acotado, nunca lanza y, si no encuentra el
 * botón, se rinde — el chat ya está abierto y el micro sigue ahí para tocarlo a mano.
 *
 * Lo difícil es CUÁNDO. El contexto sale antes con `startConversation`, que va por la red, e
 * Intercom rehace la vista cuando la conversación queda creada: una grabación arrancada antes se
 * corta en seco (visto en la tablet, 29-sep-2026: «se pone a grabar y se para inmediatamente»). El
 * SDK no avisa de que la conversación existe, así que se deduce del propio chat:
 *   1. esperar a que el mensaje de contexto esté pintado (`expectText`),
 *   2. y a que el micro lleve `stableMs` siendo el MISMO botón, con un mínimo de `minDelayMs`
 *      desde la llamada (si Intercom lo vuelve a montar, la espera empieza de cero);
 *   3. después de pulsarlo, si la grabación llega a arrancar (el botón de empezar desaparece) y el
 *      redactor se REHACE (su campo de texto pasa a ser otro elemento) y vuelve vacío con el micro,
 *      es que la conversación se creó tarde y la cortó: se pulsa otra vez, una sola, dentro de
 *      `cutMs`. Cuando es la persona la que para, el campo es el mismo y la transcripción cae
 *      dentro; eso no se toca nunca, porque relanzar la grabación la pisaría.
 *
 * Además quita el foco del redactor si lo tuviera: con el foco dentro, en la tablet sube el teclado
 * y tapa media pantalla justo cuando lo que se quiere es hablar.
 *
 * @param {object} [opts]
 * @param {string|null} [opts.expectText]  un trozo del mensaje de contexto que tiene que verse antes
 * @param {() => void} [opts.onPressed]    en cuanto se pulsa (para decirle a la persona que hable)
 * @param {boolean} [opts.acceptCurrent]   el micro que ya está a la vista vale: la conversación es
 *                                         la misma que sigue abierta, no una que se esté creando
 * @returns {Promise<{ result: 'pressed'|'repressed'|'no_recording'|'not_found', waitMs: number, remounts: number }>}
 *   `pressed` grabó; `repressed` se cortó y se volvió a pulsar; `no_recording` se pulsó pero nunca
 *   llegó a grabar (el permiso sin contestar, por ejemplo); `not_found` no hubo micro que pulsar.
 */
export function startIntercomVoiceNote({
  doc = typeof document !== 'undefined' ? document : null,
  expectText = null,
  onPressed = null,
  acceptCurrent = false,
  timeoutMs = 20000,
  intervalMs = 150,
  minDelayMs = 1500,
  stableMs = 1200,
  recordingMs = 10000,
  cutMs = 8000,
} = {}) {
  if (!doc) return Promise.resolve({ result: 'not_found', waitMs: 0, remounts: 0 });
  const startedAt = Date.now();
  // El micro que ya estaba a la vista ANTES de que Intercom abra la conversación nueva es el de la
  // anterior (el chat ya estaba abierto). Solo vale uno nuevo; al reabrir, Intercom pinta el botón
  // de cero (medido). Siguiendo en la misma conversación no hay otra que esperar: vale el de ahora.
  let stale = null;
  if (!acceptCurrent) {
    try { stale = findVoiceButton(doc); } catch (_) { stale = null; }
  }

  let seen = null;          // el último micro visto, para saber si es el mismo botón
  let seenSince = startedAt;
  let remounts = 0;
  let pressedAt = 0;
  let fieldAtPress = null;  // el campo de texto de la vista en la que se pulsó
  let recordingAt = 0;      // cuándo desapareció el botón de empezar: está grabando
  let phase = 'waiting';    // waiting → pressed → recording

  const press = (mic) => {
    const field = findComposer(doc);
    if (field && typeof field.blur === 'function') field.blur();
    mic.click();
  };

  return new Promise((resolve) => {
    const finish = (result) => resolve({ result, waitMs: (pressedAt || Date.now()) - startedAt, remounts });
    const tick = () => {
      const now = Date.now();
      try {
        const found = findVoiceButton(doc);
        const mic = found && found !== stale ? found : null;
        if (phase === 'waiting') {
          if (mic !== seen) {
            if (mic && seen) remounts += 1;
            seen = mic;
            seenSince = now;
          }
          const settled = mic && now - startedAt >= minDelayMs && now - seenSince >= stableMs;
          if (settled && messengerHasText(doc, expectText)) {
            fieldAtPress = findComposer(doc);
            press(mic);
            pressedAt = now;
            phase = 'pressed';
            if (typeof onPressed === 'function') { try { onPressed(); } catch (_) { /* no es nuestro */ } }
          } else if (now - startedAt >= timeoutMs) {
            finish('not_found');
            return;
          }
        } else if (phase === 'pressed') {
          // Grabando, el botón de empezar se va (lo sustituyen parar y cancelar).
          if (!found) { phase = 'recording'; recordingAt = now; }
          else if (now - pressedAt >= recordingMs) { finish('no_recording'); return; }
        } else if (phase === 'recording') {
          const rebuilt = found && findComposer(doc) !== fieldAtPress;
          if (rebuilt && composerEmpty(doc) && now - recordingAt < cutMs) {
            remounts += 1;
            press(found);
            finish('repressed');
            return;
          }
          if (now - recordingAt >= cutMs) { finish('pressed'); return; }
        }
      } catch (_) { /* DOM de un tercero: nunca romper el envío */ }
      setTimeout(tick, intervalMs);
    };
    tick();
  });
}

/**
 * Escribe `text` en la conversación que el messenger acaba de abrir con `showConversation` y, con
 * `send`, lo envía: así un escalado sigue en la conversación del episodio en vez de abrir otra (cuál
 * es la del episodio lo decide intercomConversation.js). Mismo contrato que el resto del módulo, más
 * la regla que manda aquí: NUNCA ENVIAR DOS VECES NI TOCAR LO QUE HA ESCRITO LA PERSONA.
 *
 *   1. Espera a que el messenger enseñe ESA conversación (`isShown`) con un redactor que lleve
 *      `settleMs` siendo el mismo elemento. Medido el 30-sep-2026: al reabrirlo (cuatro veces),
 *      mientras se anima el iframe mide 0×0 o el redactor no está, y el que aparece al final (a los
 *      0,7-1,2 s) es el que se queda; con el chat ya abierto (cuatro veces), el redactor que había se va
 *      a los ~30 ms y el nuevo sale a los 0,4-0,5 s y se queda. El asentamiento se cuenta desde ese.
 *   2. Si el redactor ya tiene algo escrito, no lo toca.
 *   3. Pega el texto, y si no ha entrado entero no envía nada.
 *   4. Pulsa enviar y comprueba que el texto ha salido del redactor y está en el hilo (Intercom lo
 *      pinta al momento, antes de que responda el servidor; medido: 8 ms).
 *   5. Si al final no ha salido, quita del redactor lo que pegó: si se quedara, alguien podría
 *      enviarlo después a mano, y soporte lo recibiría dos veces.
 *
 * @param {object} opts
 * @param {string|null} opts.text  lo que hay que mandar; null solo espera al redactor (una nota de voz
 *                                 sin mensaje delante)
 * @param {boolean} [opts.send]    false lo deja escrito, para que la persona lo revise y lo envíe
 * @param {() => boolean} [opts.isShown]  si el messenger enseña la conversación que se espera
 * @returns {Promise<{ result: string, waitMs: number, left: boolean }>} con `result`:
 *   - `sent` (salió y está en el hilo), `typed` (escrito sin enviar) o `ready` (redactor listo, sin texto);
 *   - `no_composer` (la conversación no llegó a abrirse, o Intercom no deja escribir en ella),
 *     `draft` (había algo escrito), `not_typed` (el texto no entró) y `no_send` (no hubo botón, o
 *     pulsarlo no envió): en los cuatro NO HA SALIDO NADA y se puede abrir una conversación nueva;
 *   - `unconfirmed`: se pulsó enviar y no se pudo comprobar. Puede haber salido: no se reintenta.
 *   `left` dice si se quedó en el redactor algo de lo que pegó la app (no se pudo quitar).
 */
export function replyInIntercomConversation({
  doc = typeof document !== 'undefined' ? document : null,
  text = null,
  send = true,
  isShown = () => true,
  timeoutMs = 6000,
  settleMs = 500,
  typeMs = 1500,
  confirmMs = 5000,
  intervalMs = 150,
} = {}) {
  if (!doc) return Promise.resolve({ result: 'no_composer', waitMs: 0, left: false });
  const startedAt = Date.now();
  const lines = String(text || '').split('\n').map(squash).filter(Boolean);
  const first = lines[0] || '';
  const last = lines[lines.length - 1] || '';
  // Para saber si lo que hay en el redactor es todo nuestro: sin espacios, que el redactor junta los
  // párrafos sin separador (y si algún día mete uno, que no cambie la respuesta).
  const bare = (value) => String(value || '').replace(/\s+/g, '');
  const ours = bare(lines.join(''));
  let field = null;
  let fieldSince = startedAt;
  let phase = 'opening';  // opening → typing → sending, y takingBack si hay que deshacer
  let before = 0;         // cuántas veces estaba ya la primera línea en el hilo (el mismo tema dos veces)
  let pastedAt = 0;
  let pressedAt = 0;
  let outcome = null;     // lo que se devolverá después de deshacer
  let backStep = 0;
  let backAt = 0;

  return new Promise((resolve) => {
    const finish = (result, left = false) => resolve({ result, waitMs: Date.now() - startedAt, left });
    const composerText = () => squash(fieldText(findComposer(doc)));
    // No ha salido nada: antes de decirlo, se quita lo que pegó la app. Solo si todo lo que hay en el
    // redactor es suyo; si hay algo más, alguien ha escrito y no se toca.
    const giveBack = (result) => {
      const current = bare(fieldText(findComposer(doc)));
      if (!current) { finish(result); return; }
      if (!ours.includes(current)) { finish(result, true); return; }
      outcome = result;
      phase = 'takingBack';
      backAt = Date.now();
    };
    const tick = () => {
      const now = Date.now();
      try {
        if (phase === 'opening') {
          const found = isShown() ? findComposer(doc) : null;
          if (found !== field) { field = found; fieldSince = now; }
          if (field && now - fieldSince >= settleMs) {
            if (!composerEmpty(doc)) { finish('draft'); return; }
            if (!lines.length) { finish('ready'); return; }
            before = timesOnThread(doc, first);
            if (!pasteInto(field, text)) { finish('not_typed'); return; }
            pastedAt = now;
            phase = 'typing';
          } else if (now - startedAt >= timeoutMs) {
            finish('no_composer');
            return;
          }
        } else if (phase === 'typing') {
          const typed = composerText();
          const whole = typed.includes(first) && typed.includes(last);
          if (whole && !send) { finish('typed'); return; }
          // El botón se habilita un instante después de que entre el texto: se le espera.
          const button = whole ? findSendButton(doc) : null;
          if (button) {
            button.click();
            pressedAt = now;
            phase = 'sending';
          } else if (!whole && now - pastedAt >= typeMs) {
            // Pegado, el texto entra en el mismo instante; si no ha entrado ya, no va a entrar (un
            // navegador que no deja pegar así): cuanto antes se abra la nueva, menos espera la cocina.
            giveBack('not_typed');
            if (phase !== 'takingBack') return;
          } else if (whole && now - pastedAt >= confirmMs) {
            // Con el texto dentro, el botón sí puede tardar (el redactor bloqueado mientras Fin contesta).
            giveBack('no_send');
            if (phase !== 'takingBack') return;
          }
        } else if (phase === 'sending') {
          const onThread = timesOnThread(doc, first) > before;
          if (onThread && composerEmpty(doc)) { finish('sent'); return; }
          if (now - pressedAt >= confirmMs) {
            // En el hilo está: ha salido, aunque Intercom no haya vaciado el redactor (un reintento, un
            // repintado lento). Y si no está ni en el hilo ni en el redactor, no se sabe: puede haber
            // salido. En los dos casos, nada de abrir otra con el mismo mensaje.
            if (onThread || !composerText().includes(first)) { finish('unconfirmed', composerText().includes(first)); return; }
            // Sigue en el redactor y no en el hilo: pulsar no envió.
            giveBack('no_send');
            if (phase !== 'takingBack') return;
          }
        } else {
          const current = findComposer(doc);
          if (!squash(fieldText(current))) { finish(outcome); return; }
          if (backStep === 0) { selectContents(current); backStep = 1; }
          else if (backStep === 1) { cutFrom(current); backStep = 2; }
          else if (now - backAt >= typeMs) { finish(outcome, true); return; }
        }
      } catch (_) {
        // DOM de un tercero. Antes de pulsar no ha salido nada; después, puede que sí.
        if (phase === 'sending') finish('unconfirmed');
        else if (phase === 'opening') finish('no_composer');
        else finish(outcome || 'not_typed', true);
        return;
      }
      setTimeout(tick, intervalMs);
    };
    tick();
  });
}

/**
 * Sondea hasta `timeoutMs` a que aparezca el redactor y le pone el foco. El sondeo existe porque
 * el messenger tarda en montar la vista de conversación después de abrirla.
 *
 * @returns {Promise<boolean>} si llegó a enfocarlo (para telemetría; nadie debe bloquearse en esto)
 */
export function focusIntercomComposer({ doc = typeof document !== 'undefined' ? document : null, timeoutMs = 4000, intervalMs = 250 } = {}) {
  if (!doc) return Promise.resolve(false);
  return new Promise((resolve) => {
    const deadline = Date.now() + timeoutMs;
    const attempt = () => {
      try {
        const el = findComposer(doc);
        if (el) {
          el.focus({ preventScroll: true });
          // En Android el teclado solo sube si el foco llega dentro del gesto del usuario; si no
          // sube, el cursor ya está puesto y basta un toque en vez de dos.
          resolve(true);
          return;
        }
      } catch (_) { /* adorno: nunca romper el envío */ }
      if (Date.now() >= deadline) { resolve(false); return; }
      setTimeout(attempt, intervalMs);
    };
    attempt();
  });
}
